import React, { useEffect, useState } from "react";
import AuthLayout from "./AuthLayout";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft, RefreshCw } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { RoutesLink } from "@/utility/RouteLinks";
import { callApi, ThrowError } from "@/api/callApi";
import { API_ENDPOINT } from "@/api/constant";
import { showToastNotification } from "@/utility/helper";
import FormField from "@/components/common/FormField";
import { useForm, type SubmitHandler } from "react-hook-form";
import { errorTypes } from "@/utility/validation";

interface IVerifyOtpForm {
    otp: string;
}

interface IVerifyOtpResponse {
    status?: boolean;
    statusCode?: number;
    message?: string;
    data?: {
        otp: string,
        deviceId: string
    };
}

const VerifyOtp: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const email = location.state?.email;
    const otp = location.state?.otp;
    const deviceId = location.state?.deviceId;

    const [isLoading, setIsLoading] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [timer, setTimer] = useState(30);

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset
    } = useForm<IVerifyOtpForm>({ mode: "onBlur" });

    // Handle initial state setup and validation on mount/state change
    useEffect(() => {
        if (!email) {
            showToastNotification("danger", "Email not found. Please try again.");
            navigate(RoutesLink?.authRoutes?.login);
            return;
        }
        if (otp) {
            reset({ otp });
        }
    }, [email, otp, navigate, reset]);

    // Handle resend code countdown timer
    useEffect(() => {
        if (timer <= 0) return;

        const interval = setInterval(() => {
            setTimer((prev) => prev - 1);
        }, 1000);

        return () => clearInterval(interval);
    }, [timer]);

    const handleVerify: SubmitHandler<IVerifyOtpForm> = async (data) => {
        try {
            setIsLoading(true);

            const response = await callApi<IVerifyOtpResponse>(
                `${API_ENDPOINT.auth}/forget-password`,
                {
                    method: "POST",
                    data: {
                        email,
                        otp: data?.otp,
                        deviceId: deviceId,
                    },
                }
            );

            if (
                response?.statusCode === 200 ||
                response?.statusCode === 201 ||
                response?.status === true
            ) {
                showToastNotification(
                    "success",
                    response?.message || "OTP Verified Successfully"
                );

                navigate(RoutesLink?.authRoutes?.resetPassword, {
                    state: {
                        email,
                        otp: data?.otp,
                        deviceId: deviceId,
                        // email: email
                    },
                });
            } else {
                showToastNotification(
                    "danger",
                    response?.message || "Invalid OTP"
                );
            }
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleResend = async () => {
        if (timer > 0) return;

        try {
            setIsResending(true);

            const response: any = await callApi(
                `${API_ENDPOINT.auth}/resend-otp`,
                {
                    method: "POST",
                    data: {
                        email,
                    },
                }
            );

            if (
                response?.statusCode === 200 ||
                response?.statusCode === 201 ||
                response?.status === true
            ) {
                showToastNotification(
                    "success",
                    response?.message || "OTP Sent Successfully"
                );

                reset();
                setTimer(30);
            } else {
                showToastNotification(
                    "danger",
                    response?.message || "Unable to resend OTP"
                );
            }
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsResending(false);
        }
    };

    return (
        <AuthLayout>
            <div className="mb-8 text-center lg:text-left">
                <h2 className="text-3xl font-bold text-slate-900 mb-2">
                    Verify OTP
                </h2>

                <p className="text-slate-500">
                    Enter the 6-digit verification code sent to your registered
                    email address.
                </p>

                {email && (
                    <p className="mt-2 text-sm font-medium text-slate-700">
                        {email}
                    </p>
                )}
            </div>

            <form onSubmit={handleSubmit(handleVerify)} className="space-y-6">

                <FormField
                    label="Enter OTP"
                    placeholder="e.g. 123456"
                    type="text"
                    maxLength={6}
                    {...register('otp', {
                        ...errorTypes.required('OTP'),
                        minLength: { value: 6, message: "OTP must be exactly 6 digits" },
                        maxLength: { value: 6, message: "OTP must be exactly 6 digits" },
                        pattern: { value: /^[0-9]+$/, message: "OTP must contain only numbers" }
                    })}
                    error={errors.otp?.message}
                />

                <Button
                    type="submit"
                    className="w-full h-11 cursor-pointer"
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                        "Verify OTP"
                    )}
                </Button>

                <div className="text-center">

                    {timer > 0 ? (
                        <p className="text-sm text-slate-500">
                            Didn't receive the code?{" "}
                            <span className="font-semibold text-primary">
                                Resend in {timer}s
                            </span>
                        </p>
                    ) : (
                        <button
                            type="button"
                            onClick={handleResend}
                            disabled={isResending}
                            className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline cursor-pointer"
                        >
                            {isResending && (
                                <RefreshCw className="h-4 w-4 animate-spin" />
                            )}
                            Resend OTP
                        </button>
                    )}

                </div>

                <div className="flex justify-center">

                    <button
                        type="button"
                        onClick={() =>
                            navigate(RoutesLink.authRoutes.login)
                        }
                        className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 transition-colors cursor-pointer"
                    >
                        <ArrowLeft size={16} />
                        Back to Sign In
                    </button>

                </div>

            </form>
        </AuthLayout>
    );
};

export default VerifyOtp;
